import { useTranslation } from 'react-i18next'
import {
  Button,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  softBadgeColors,
} from '@structural-climbing/ui'
import { gradeDisplay, type Discipline } from '@structural-climbing/validation'
import { useGymGradeConfig } from '../../layouts/useGymGradeConfig'
import { holdColorName } from './RouteIdentity'
import type { CompetitionRouteRef } from './types'

interface Props {
  /** null while closed. */
  route: CompetitionRouteRef['route']
  /** The route as staff name it: its number, with the wall in a combined competition. */
  title: string
  discipline: Discipline
  sectionName?: string | null
  roundName?: string | null
  /** How many competitors have logged it. Omitted where results are not loaded. */
  topCount?: number
  apiUrl: string
  /** Offers Edit when given. Left out where the route cannot change any more. */
  onEdit?: () => void
  onClose: () => void
}

/**
 * A competition route in full: the whole photo and everything the gym knows
 * about it. The gym's own RouteDetailModal cannot stand in, because it expects
 * a graded route with ratings, and a competition route is numbered and often
 * ungraded.
 */
export function CompetitionRouteDetailModal({
  route,
  title,
  discipline,
  sectionName,
  roundName,
  topCount,
  apiUrl,
  onEdit,
  onClose,
}: Props) {
  const { t, i18n } = useTranslation()
  const gradeConfig = useGymGradeConfig()

  return (
    <Modal
      isOpen={!!route}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      size="lg"
      scrollBehavior="inside"
    >
      <ModalContent>
        {() => {
          if (!route) return null
          const grade =
            route.grade === null ? null : gradeDisplay(gradeConfig, route.grade, discipline)
          const badge = grade ? softBadgeColors(grade.color) : null
          const hold = holdColorName(route.color)
          // Day first whatever the browser says; plain "en" would put the month first.
          const setOn = route.created_at
            ? new Date(route.created_at).toLocaleDateString(
                i18n.language.startsWith('en') ? 'en-GB' : i18n.language,
              )
            : null
          const facts: [string, string][] = []
          if (sectionName) facts.push([t('routes.section'), sectionName])
          if (roundName) facts.push([t('competitions.roundAssign'), roundName])

          return (
            <>
              <ModalHeader>{title}</ModalHeader>
              <ModalBody>
                <div className="flex flex-col gap-4">
                  {route.image_r2_key ? (
                    <img
                      src={`${apiUrl}/climbing-routes/${route.id}/image`}
                      alt={title}
                      className="max-h-[60vh] w-full rounded-lg object-contain"
                    />
                  ) : (
                    <div className="flex h-32 w-full items-center justify-center rounded-lg bg-default-100">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-16 w-16 text-foreground/20"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-2">
                    {/* A competition route has no grade: its number already names it. */}
                    {grade && badge && (
                      <Chip
                        size="sm"
                        radius="sm"
                        className="font-semibold"
                        style={{ backgroundColor: badge.bg, color: badge.fg }}
                      >
                        {t('competitions.gradeLabel', { grade: grade.label })}
                      </Chip>
                    )}
                    <Chip
                      size="sm"
                      variant="flat"
                      startContent={
                        <span
                          className="ml-1 h-3 w-3 flex-none rounded-full border border-black/10 shadow-sm"
                          style={{ backgroundColor: route.color ?? '#d4d4d8' }}
                        />
                      }
                    >
                      {hold ?? t('competitions.holdColor')}
                    </Chip>
                    {route.competition_only && (
                      <Chip size="sm" variant="flat" color="warning">
                        {t('competitions.competitionOnlyBadge')}
                      </Chip>
                    )}
                    {topCount !== undefined && (
                      <span className="ml-auto text-sm text-foreground/50">
                        {t('competitions.routeTops', { count: topCount })}
                      </span>
                    )}
                  </div>

                  {facts.length > 0 && (
                    <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                      {facts.map(([name, value]) => (
                        <div key={name} className="contents">
                          <span className="text-foreground/50">{name}</span>
                          <span>{value}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {(route.setter?.name || setOn) && (
                    <p className="text-sm text-foreground/60">
                      {[
                        route.setter?.name && t('routes.setterLabel', { name: route.setter.name }),
                        setOn && t('competitions.routeSetOn', { date: setOn }),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  )}

                  {route.setter_note && (
                    <div className="rounded-lg bg-content2 p-3">
                      <p className="mb-1 text-xs font-semibold text-foreground/60">
                        {t('routes.setterNoteTitle')}
                      </p>
                      <p className="whitespace-pre-line text-sm text-foreground/80">
                        {route.setter_note}
                      </p>
                    </div>
                  )}
                </div>
              </ModalBody>
              <ModalFooter>
                {onEdit && (
                  <Button color="primary" variant="flat" onPress={onEdit}>
                    {t('common.edit')}
                  </Button>
                )}
                <Button variant="flat" onPress={onClose}>
                  {t('common.close')}
                </Button>
              </ModalFooter>
            </>
          )
        }}
      </ModalContent>
    </Modal>
  )
}
